/**
 * FinaleSequence.ts
 * State machine for the Level 1-1 launch gate → ship takeoff cinematic.
 * Beats: APPROACH → GATE_OPENING → TRAVERSE → BOARDING → RAMP_RAISING →
 * IGNITION → TAKEOFF → OFFSCREEN → FADE_OUT.
 *
 * No React imports. No async in update(). Module-level `finaleState` is
 * read by the renderer each frame.
 * @module FinaleSequence
 */

import { gameState } from '../engine/gameState';
import { inputState, clearAllInputState } from '../input/inputState';
import { NOVA_WIDTH } from '../../constants/gameConstants';
import Logger from '../utils/logger';
import {
  type FinaleBeat,
  finaleState,
  resetFinaleState,
  setFinaleColumns,
  easeOut,
  ignitionToWorldCoords,
  gateX, gateTopY, rampX, shipPadY, shipParkedX, shipParkedY,
  pedestalCentreX, portalX,
  PROMPT_RANGE,
  GATE_OPEN_DUR, RAMP_RAISE_DUR, AUTO_WALK_SPEED,
  BOARDING_DUR, IGNITION_DUR, TAKEOFF_DUR, OFFSCREEN_DUR, FADE_OUT_DUR,
} from './finaleState';

// Re-export so existing importers (renderer, tests) keep working.
export { finaleState } from './finaleState';
export type { FinaleBeat, FinaleStateData } from './finaleState';

/** Manages the level-end cinematic beat by beat. */
export class FinaleSequence {
  private _beat: FinaleBeat = 'NONE';
  private _timer = 0;
  /** Whether the player can skip (completed level before). */
  private _canSkip = false;

  /**
   * Initialise the finale with gate and ramp positions.
   * @param gateCol - Column of the gate left edge.
   * @param rampCol - Column of the ramp left edge.
   * @param canSkip - Whether skip is available (prior completion).
   */
  public init(gateCol: number, rampCol: number, canSkip: boolean): void {
    setFinaleColumns(gateCol, rampCol);
    this._canSkip = canSkip; this._beat = 'APPROACH'; this._timer = 0;
    resetFinaleState();
    finaleState.beat = 'APPROACH';
    finaleState.gateX = gateX(); finaleState.gateTopY = gateTopY();
    finaleState.rampX = rampX(); finaleState.rampBaseY = shipPadY();
    finaleState.shipX = shipParkedX(); finaleState.shipY = shipParkedY();
    finaleState.shipVisible = true;
    Logger.info('FinaleSequence', `Initialized gate@col${gateCol} ramp@col${rampCol}`);
  }

  /** Whether the finale is active (any beat other than NONE). */
  public get isActive(): boolean { return this._beat !== 'NONE'; }

  /** Whether the gate wall should block Nova. */
  public get gateBlocks(): boolean { return this._beat === 'APPROACH'; }

  /** The portal's X position for wall collision. */
  public get gateWallX(): number { return portalX(); }

  /** Per-tick update. @param dt - Fixed timestep in seconds. */
  public update(dt: number): void {
    this._timer += dt;
    finaleState.timer = this._timer;

    switch (this._beat) {
      case 'APPROACH':      this._tickApproach(); break;
      case 'GATE_OPENING':  this._tickGateOpening(dt); break;
      case 'TRAVERSE':      this._tickTraverse(dt); break;
      case 'BOARDING':      this._tickBoarding(); break;
      case 'RAMP_RAISING':  this._tickRampRaising(); break;
      case 'IGNITION':      this._tickIgnition(); break;
      case 'TAKEOFF':       this._tickTakeoff(dt); break;
      case 'OFFSCREEN':     this._tickOffscreen(); break;
      case 'FADE_OUT':      this._tickFadeOut(); break;
      default: break;
    }
  }

  // ── Beat handlers ─────────────────────────────────────────────────────

  /** Check if skip is requested; if so, jump to FADE_OUT. */
  private _trySkip(): boolean {
    if (this._canSkip && inputState.attackPressed) { this._skipToEnd(); return true; }
    return false;
  }

  private _tickApproach(): void {
    const nova = gameState.nova;
    // DECISION: Proximity prompt near pedestal; activate with Attack/Action.
    const novaCentreX = nova.x + NOVA_WIDTH / 2;
    const dist = Math.abs(novaCentreX - pedestalCentreX());
    finaleState.showActionPrompt = dist < PROMPT_RANGE;
    // DECISION: Mirror the prompt onto the generic engine-level flag so the
    // projectile system can suppress shard fire without importing scene state.
    gameState.suppressPlayerFire = finaleState.showActionPrompt;
    if (finaleState.showActionPrompt && inputState.attackPressed) {
      this._startBeat('GATE_OPENING');
      finaleState.cameraLocked = true;
      finaleState.lockedCamX = gameState.camera.x;
      finaleState.lockedCamY = gameState.camera.y;
      finaleState.showActionPrompt = false;
      gameState.suppressPlayerFire = false;
      // DECISION: Don't set finaleActive — player needs normal physics
      // during GATE_OPENING and TRAVERSE to reach the ship.
      gameState.level.exitPortalActive = true;
      Logger.info('FinaleSequence', 'Gate activated — portal active, auto-walk begins');
    }
  }

  private _tickGateOpening(_dt: number): void {
    const progress = Math.min(this._timer / GATE_OPEN_DUR, 1);
    finaleState.gateOpen = easeOut(progress);
    finaleState.beat = 'GATE_OPENING';
    if (this._trySkip()) return;
    if (this._timer >= GATE_OPEN_DUR) {
      finaleState.gateOpen = 1;
      // Transition to TRAVERSE — Nova auto-walks toward the ship.
      this._startBeat('TRAVERSE');
      finaleState.cameraLocked = false;
      finaleState.showSkipPrompt = this._canSkip;
    }
  }

  /**
   * Auto-walk traversal after the gate opens.
   * @param dt - Fixed timestep in seconds.
   */
  private _tickTraverse(dt: number): void {
    const nova = gameState.nova;

    // DECISION: If Nova died and respawned far from the finale area, abort
    // back to APPROACH so the player regains control.
    // Don't clear inputState here — held keys should resume immediately.
    if (nova.x < pedestalCentreX() - PROMPT_RANGE * 3) {
      this._startBeat('APPROACH');
      finaleState.cameraLocked = false;
      finaleState.showSkipPrompt = false;
      Logger.info('FinaleSequence', 'Nova respawned far from gate — reverting to APPROACH');
      return;
    }

    // DECISION: Move Nova directly at AUTO_WALK_SPEED (not via input injection
    // which would accelerate to MAX_RUN_SPEED). Zero vx to prevent residual
    // momentum from physics integration doubling the displacement.
    // Do NOT mutate inputState — that would clobber held keys and prevent
    // them from resuming if TRAVERSE aborts back to APPROACH (see review).
    nova.facingRight = true;
    nova.vx = 0;
    nova.x += AUTO_WALK_SPEED * dt;
    // DECISION: Only force RUN from neutral locomotion states so
    // HURT/DEAD/LAND animations are not overwritten during auto-walk.
    const neutralStates = ['IDLE', 'RUN', 'RUN_START', 'RUN_STOP', 'CROUCH'];
    if (nova.isGrounded && neutralStates.includes(nova.state)) nova.state = 'RUN';

    // Trigger boarding when Nova reaches the ramp X and is on the ship pad.
    // DECISION: Require isGrounded to prevent boarding while airborne (e.g. knockback).
    if (nova.x >= rampX() && nova.y <= shipPadY() && nova.isGrounded) {
      // finaleActive = true skips physics entirely — no need to clear inputState.
      gameState.finaleActive = true;
      finaleState.cameraLocked = true;
      finaleState.lockedCamX = gameState.camera.x;
      finaleState.lockedCamY = gameState.camera.y;
      nova.state = 'IDLE';
      nova.vx = 0;
      nova.vy = 0;
      this._startBeat('BOARDING');
      Logger.info('FinaleSequence', 'Nova reached ramp — boarding ship');
    }
    if (this._trySkip()) return;
  }

  /** Ramp tilts 0° → 45° over RAMP_RAISE_DUR. Nova aboard, ship rides angle. */
  private _tickRampRaising(): void {
    const progress = Math.min(this._timer / RAMP_RAISE_DUR, 1);
    // DECISION: easeOut for smooth deceleration at 45°.
    const angle = easeOut(progress) * 45;
    finaleState.rampAngle = angle;
    finaleState.shipAngle = angle;
    if (this._trySkip()) return;
    if (this._timer >= RAMP_RAISE_DUR) {
      finaleState.rampAngle = 45;
      finaleState.shipAngle = 45;
      this._startBeat('IGNITION');
      Logger.info('FinaleSequence', 'Ramp at 45° — ignition sequence');
    }
  }

  private _tickBoarding(): void {
    // DECISION: Hide Nova during boarding — ship sheet has boarding frames.
    const progress = Math.min(this._timer / BOARDING_DUR, 1);
    if (progress > 0.2) { gameState.nova.state = 'IDLE'; gameState.nova.x = -999; }
    finaleState.crystalGlow = 0.5 + progress * 0.3;
    if (this._trySkip()) return;
    if (this._timer >= BOARDING_DUR) {
      this._startBeat('RAMP_RAISING');
      Logger.info('FinaleSequence', 'Nova aboard — raising ramp to 45°');
    }
  }

  private _tickIgnition(): void {
    const progress = Math.min(this._timer / IGNITION_DUR, 1);
    finaleState.crystalGlow = 0.5 + Math.sin(progress * Math.PI * 3) * 0.5;
    // DECISION: flame transitions idle → cruise during ignition.
    finaleState.flameMode = progress < 0.3 ? 'IDLE' : 'CRUISE';
    finaleState.flameCrossfade = progress < 0.3
      ? Math.min(progress / 0.2, 1)
      : Math.min((progress - 0.3) / 0.2, 1);
    const shakeMag = progress * 2;
    finaleState.shakeX = (Math.random() - 0.5) * shakeMag;
    finaleState.shakeY = (Math.random() - 0.5) * shakeMag;
    finaleState.dustActive = progress > 0.3;
    if (this._trySkip()) return;
    if (this._timer >= IGNITION_DUR) {
      // DECISION: Convert ramp-rotated coords to world coords for free flight.
      const worldPos = ignitionToWorldCoords();
      finaleState.shipX = worldPos.x;
      finaleState.shipY = worldPos.y;
      this._startBeat('TAKEOFF');
    }
  }

  private _tickTakeoff(dt: number): void {
    const progress = Math.min(this._timer / TAKEOFF_DUR, 1);
    // DECISION: High speed + acceleration; renderer fades globalAlpha.
    const speed = 600 + progress * 1400;
    const rad = (45 * Math.PI) / 180;
    finaleState.shipX += Math.cos(rad) * speed * dt;
    finaleState.shipY -= Math.sin(rad) * speed * dt;
    finaleState.flameMode = 'BOOST';
    finaleState.flameCrossfade = 1;
    const shakeMag = 3 - progress * 2;
    finaleState.shakeX = (Math.random() - 0.5) * shakeMag;
    finaleState.shakeY = (Math.random() - 0.5) * shakeMag;
    finaleState.dustActive = progress < 0.3;
    finaleState.crystalGlow = Math.max(0, 1 - progress * 3);
    if (this._trySkip()) return;
    if (this._timer >= TAKEOFF_DUR) {
      finaleState.shipVisible = false;
      this._startBeat('OFFSCREEN');
    }
  }

  private _tickOffscreen(): void {
    const progress = Math.min(this._timer / OFFSCREEN_DUR, 1);
    finaleState.shakeX = 0; finaleState.shakeY = 0;
    finaleState.dustActive = false; finaleState.sonicBoom = progress;
    if (this._trySkip()) return;
    if (this._timer >= OFFSCREEN_DUR) {
      this._startBeat('FADE_OUT');
    }
  }

  private _tickFadeOut(): void {
    const progress = Math.min(this._timer / FADE_OUT_DUR, 1);
    finaleState.fadeAlpha = progress;
    if (progress >= 1) {
      this._beat = 'NONE';
      finaleState.beat = 'NONE';
      gameState.levelComplete = true;
      Logger.info('FinaleSequence', 'Cinematic complete — levelComplete set');
    }
  }

  private _startBeat(beat: FinaleBeat): void {
    this._beat = beat; this._timer = 0;
    finaleState.beat = beat; finaleState.timer = 0;
    Logger.debug('FinaleSequence', `Beat: ${beat}`);
  }

  private _skipToEnd(): void {
    Logger.info('FinaleSequence', 'Cutscene skipped');
    // DECISION: Freeze gameplay on skip — finaleActive causes stepPhysics to
    // short-circuit, and clearAllInputState prevents stale held-key movement.
    gameState.finaleActive = true;
    clearAllInputState();
    finaleState.shipVisible = false; finaleState.dustActive = false;
    finaleState.shakeX = 0; finaleState.shakeY = 0;
    gameState.nova.x = -999;
    this._startBeat('FADE_OUT');
  }

  /** Clean up on scene destroy. */
  public destroy(): void {
    this._beat = 'NONE';
    // DECISION: Use clearAllInputState (engine-safe) rather than manually
    // zeroing individual flags. Keyboard heldKeys resync on next key event.
    clearAllInputState();
    resetFinaleState();
  }
}
